/** Perspective camera framing, depth coverage and scenery placement. Geometry is pure so projection invariants can be tested without a GPU. */

import { VIEW_H, VIEW_W } from '../engine/constants'

/**
 * Vertical field of view, in degrees.
 *
 * Three.js's `fov` is vertical, which is why the distance below is derived from
 * the view *height*. These two are not independent: fixing the fov and the view
 * size fixes the distance, so there is one framing knob, not two.
 */
export const CAM_FOV_DEG = 22

/** Side-on framing keeps the play plane rectangular and collision positions
 * aligned with the artwork. Perspective still reveals depth on extruded props
 * and gives the distant scenery parallax as the camera scrolls. */
export const CAM_PITCH_DEG = 0

/** How far above the top of the view the cloud band begins, in world pixels. */
export const CLOUD_BAND_MARGIN = 40

/**
 * Distance from the camera to the play plane, along the view axis.
 *
 * Derived so the art viewport is exactly the framing: at this distance the
 * frustum subtends `VIEW_H` of world height. A hand-picked distance would zoom
 * the game in or out with no way to tell from the code, which is the sort of
 * thing that only reveals itself as "why is Tavi smaller than he was".
 */
export function cameraDistance(viewH = VIEW_H): number {
  return viewH / 2 / Math.tan((CAM_FOV_DEG * Math.PI) / 360)
}

/**
 * The vertical field of view needed to frame `viewH` of world at the play plane.
 *
 * Exists so the renderer can ask for the fov matching the rect it is drawing
 * rather than scaling one. The editor's level view is a different size from the
 * game's, and framing the whole buffer then scissoring to the inset rect showed
 * only the middle of the scene, stretched into a trapezoid.
 */
export function verticalFovFor(viewH = VIEW_H): number {
  return (2 * Math.atan(viewH / 2 / cameraDistance(viewH)) * 180) / Math.PI
}

/** How far above the aim point the camera sits. */
export function cameraLift(distance = cameraDistance()): number {
  return Math.sin((CAM_PITCH_DEG * Math.PI) / 180) * distance
}

/** How far back along Z the camera sits. */
export function cameraBack(distance = cameraDistance()): number {
  return Math.cos((CAM_PITCH_DEG * Math.PI) / 180) * distance
}

/**
 * Where the camera goes, in the renderer's axes.
 *
 * Three.js's Y is up and the simulation's is down, so every world Y is negated
 * on the way in and the lift is then *added* to the negated centre. Computing
 * the lift against an un-negated centre is the sign error that put the camera
 * underground, so the negation happens here, once, and nowhere else.
 */
export function cameraPlacement(
  camX: number,
  camY: number,
  viewW = VIEW_W,
  viewH = VIEW_H,
): { x: number; y: number; z: number; aimX: number; aimY: number } {
  const cx = camX + viewW / 2
  const cy = camY + viewH / 2
  const d = cameraDistance(viewH)
  return {
    x: cx,
    y: -cy + cameraLift(d),
    z: cameraBack(d),
    aimX: cx,
    aimY: -cy,
  }
}

/**
 * How much bigger than the view a full-frame quad must be to cover the frustum
 * at depth `z`.
 *
 * The frustum widens with distance, so anything behind the play plane needs
 * scaling up by the ratio of distances. A backdrop sized to the view and parked
 * behind the play plane appears as a small rectangle in the middle of a black
 * screen -- which is exactly how the first perspective build looked, and a bug
 * that is unmissable in a screenshot and invisible in the scene graph.
 */
export function frustumCoverAt(z: number, distance = cameraDistance()): number {
  const near = Math.max(1, distance)
  const at = Math.max(1, distance - z)
  return at / near
}

/** Extra cover for the corners: the quad is flat and the frustum is a cone. */
export const SKY_COVER_MARGIN = 1.9

/**
 * The world x range the camera can see at depth `z`, with a margin.
 *
 * Solved from the frustum rather than guessed, because culling to the wrong
 * range makes distant layers pop in and out at the screen edges.
 */
export function worldRangeAtDepth(
  camX: number,
  z: number,
  margin: number,
  viewW = VIEW_W,
  viewH = VIEW_H,
): [number, number] {
  const cx = camX + viewW / 2
  // The world x span visible on the plane at depth `z`, as a multiple of the
  // span visible on the play plane.
  //
  // Not a distance ratio. The camera is tilted, so "distance to the plane along
  // the view axis" and "distance along the ray to where that plane is" differ,
  // and using the former understates the span by a wide margin -- the backdrop
  // range came out *narrower* than the view, which is backwards: something further
  // away always covers more world per pixel.
  const ratio = depthRatio(z, viewH)
  const halfWorld = (viewW / 2) * ratio
  return [cx - halfWorld * (1 + margin), cx + halfWorld * (1 + margin)]
}

/**
 * How much more world is visible at depth `z` than on the play plane.
 *
 * Solved from the ray rather than the camera's position, because the tilt is what
 * makes the two differ. At `z = 0` this is exactly 1 by construction, and it
 * grows with distance, which is the property everything else depends on.
 */
export function depthRatio(z: number, viewH = VIEW_H): number {
  const d = cameraDistance(viewH)
  const back = cameraBack(d)
  // The ray's z component, from a camera pitched down by CAM_PITCH_DEG.
  const rayZ = -Math.cos((CAM_PITCH_DEG * Math.PI) / 180)
  // Ray parameter where the ray crosses z=0, and where it crosses z.
  const t0 = -back / rayZ
  const tz = (z - back) / rayZ
  if (t0 === 0) return 1
  return Math.abs(tz / t0)
}

/**
 * How much further the cull range must reach than the view, in world pixels.
 *
 * The camera is lifted, so the *near* edge of the frustum -- the bottom of the
 * screen, looking down at nearby ground -- is closer than the focal plane and
 * therefore sees more world than the buffer is tall. The top of the screen is
 * further away and sees less.
 *
 * So the pad is asymmetric, and that is not a refinement: the first version
 * derived it symmetrically and got zero, because it computed the magnification at
 * the aim point's depth and compared it against the view height, which are equal
 * by construction. Culling then used no margin at all.
 */
export function worldPad(z = 0, viewH = VIEW_H): { x: number; y: number } {
  // At the play plane the ratio is 1, so the pad is zero. Terrain is culled
  // against the view rectangle and the pool releases anything not drawn, which is
  // correct here: the play plane is where the camera is focused, so there is
  // nothing beyond it to reveal. The pad exists for *inclined* planes, where the
  // near edge sees more than the focal plane.
  const ratio = depthRatio(z, viewH)
  return {
    x: (VIEW_W / 2) * ratio - VIEW_W / 2,
    y: (viewH / 2) * ratio - viewH / 2,
  }
}

/**
 * The vertical band the cloud field is spread across, for a given camera.
 *
 * Tied to the camera rather than the level, and that is the whole point. A
 * course is 30 tiles tall while the camera sees about 180px of it, so any
 * level-relative band puts most of the clouds outside the viewport -- which is
 * what produced an empty sky with nothing to explain it.
 */
export function cloudBand(
  camY: number,
  viewH = VIEW_H,
): { top: number; span: number } {
  return {
    top: camY - CLOUD_BAND_MARGIN,
    // Biased into the top of the view, because that is where the sky is: a cloud
    // low in the band sits behind the terrain. 40% of the view height, so the
    // lowest row is still above the ground line in a typical framing.
    span: (viewH + CLOUD_BAND_MARGIN * 2) * 0.4,
  }
}

/** World y for a cloud in `row` of `rows`, biased toward the top of the view. */
export function cloudRowY(
  camY: number,
  row: number,
  rows: number,
  jitter = 0,
  viewH = VIEW_H,
): number {
  const { top, span } = cloudBand(camY, viewH)
  return top + ((row + jitter) / rows) * span
}
